import arrowRightIcon from '@raison/static/icons/arrow-right-s-line.svg';
import React, {Fragment, ReactElement, ReactNode} from 'react';
import {PolyElement, createPolymorphicComponent} from '../../Utilities/createPolymorphicComponent';
import {IconProp} from '../shared/props';
import {ModifierClasses} from '../shared/types';
import scss from './RsnIconWidget.module.scss';

type Modifier<Base extends string> = ModifierClasses<Base, typeof scss>;

interface BaseProps extends RsnIconWidget.Props {
    spacing: Modifier<'spacing'>;
}

const RsnIconWidgetBase = ({
    children: child,
    spacing,
    addon,
    icon: {size: iconSize = 'md', ...icon},
    font = 'regular',
    className = '',
    ...rest
}: BaseProps) => (
    <PolyElement
        {...rest}
        className={`${className} ${scss.container} ${scss[`spacing--${spacing}`]} ${scss[`font--${font}`]}`}
    >
        <figure className={`${scss.icon} ${scss[`icon-size--${iconSize}`]}`}>{IconProp.render(icon)}</figure>
        {child}
        {addon}
    </PolyElement>
);

namespace RsnIconWidget {
    export interface Props {
        icon: IconProp & {
            size?: Modifier<'icon-size'>;
        };
        /** Only one child element is allowed since the other is `addon` */
        children: ReactElement;
        /** Any element no be placed on the right side of widget */
        addon?: ReactNode;
        /** Sets font for headings */
        font?: Modifier<'font'>;
        className?: string;
    }
}

/**
 * Spacing 8px
 */
const Sm = createPolymorphicComponent('div')<RsnIconWidget.Props>(props =>
    RsnIconWidgetBase({
        ...props,
        spacing: 'sm',
    })
);
Sm.displayName = `RsnIconWidget.Sm`;

/**
 * Spacing 12px
 */
const Md = createPolymorphicComponent('div')<RsnIconWidget.Props>(props =>
    RsnIconWidgetBase({
        ...props,
        spacing: 'md',
    })
);
Md.displayName = `RsnIconWidget.Md`;

/**
 * Spacing 16px
 */
const Lg = createPolymorphicComponent('div')<RsnIconWidget.Props>(props =>
    RsnIconWidgetBase({
        ...props,
        spacing: 'lg',
    })
);
Lg.displayName = `RsnIconWidget.Lg`;

//
// TextBlock
//

/**
 * Two lines of text, either one can be absent leaving empty space above or below.
 * If either is to large it overflows with ellipsis, but after-sections will not be hidden
 */
const TextBlock = ({
    headingColor,
    heading,
    details,
    afterDetails,
    afterHeading,
    multiline,
}: {
    heading?: ReactNode;
    details?: ReactNode;
    afterHeading?: ReactNode;
    afterDetails?: ReactNode;
    headingColor?: Modifier<'heading'>;
    /** Switches details overflow behavior to make it wrap on new lines */
    multiline?: boolean;
}) => (
    <Fragment>
        {!heading ? null : (
            <sup className={`${scss.heading} ${headingColor ? scss[`heading--${headingColor}`] : ''}`}>
                <span className={scss.overflow}>{heading}</span>
                {afterHeading}
            </sup>
        )}
        {!details ? null : (
            <sub className={`${scss.details} ${multiline ? scss.multiline : ''}`}>
                <span className={scss.overflow}>{details}</span>
                {afterDetails}
            </sub>
        )}
    </Fragment>
);
TextBlock.displayName = `RsnIconWidget.TextBlock`;

//
// Text
//

/**
 * Single centered line of text
 */
const Text = (props: {children: ReactNode; className?: string; after?: ReactNode}) => (
    <Fragment>
        <div className={`${scss.heading} ${scss.span2rows} ${props.className}`}>
            <span className={scss.overflow}>{props.children}</span>
            {props.after}
        </div>
    </Fragment>
);
Text.displayName = `RsnIconWidget.Text`;

const Arrow = (props: {label?: string}) => (
    <div className={scss.arrowBox}>
        {props.label}
        <img width='20' height='20' src={arrowRightIcon} />
    </div>
);
Arrow.displayName = `RsnIconWidget.Arrow`;

const framedIcon = (src: string) => ({
    element: <div className={scss.iconFrame}>{IconProp.render({src})}</div>,
});

/**
 * @deprecated
 */
const RsnIconWidget = {Sm, Md, Lg, TextBlock, Text, Arrow, framedIcon};

export default RsnIconWidget;
